import { useEffect, useId, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Send, X } from 'lucide-react'
import { Button, Skeleton } from '../../core/index.js'
import type { FeedbackCategory, FeedbackItem, FeedbackScreen } from '../index.js'
import { Switch } from '../../core/index.js'
import { EndpointHint } from '../../core/index.js'
import type { FeedbackApi } from './feedbackApi.js'
import { captureScreen } from './screenContext.js'
import { DEFAULT_FEEDBACK_CATEGORY, FEEDBACK_CATEGORY_OPTIONS, feedbackCategoryOption } from './feedbackCategories.js'
import { FeedbackCategoryBadge, FeedbackStatusBadge, ResolutionLine, formatWhen } from './FeedbackStatus.js'
import { LoadFailure } from '../../core/index.js'

const COMMENT_MAX_LENGTH = 4000

export type ScreenDetails = Readonly<Record<string, string | null | undefined>>

interface FeedbackDrawerProps {
  api: FeedbackApi
  /** What the shell knows about the open screen, sent only when the person includes the screen. */
  details: ScreenDetails
  /** Matches the button's position, so the panel opens just above it. */
  aboveInspector?: boolean
  onClose: () => void
}

function ScreenPreview({ screen }: { screen: FeedbackScreen }) {
  const pairs: [string, string][] = [
    ['Screen', `${screen.title || screen.path} (${screen.path})`],
    ['Browser', `${screen.viewport} · ${screen.userAgent}`],
    ...Object.entries(screen.details ?? {}),
  ]
  return (
    <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px]">
      {pairs.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="font-bold text-ink-mid">{label}</dt>
          <dd className="text-ink break-all">{value}</dd>
        </div>
      ))}
    </dl>
  )
}

function MyFeedbackList({ items }: { items: FeedbackItem[] }) {
  if (items.length === 0) {
    return <p className="text-[11px] text-ink-mid">Nothing sent yet.</p>
  }
  return (
    <ul className="flex flex-col gap-2">
      {items.map((item) => (
        <li key={item.feedbackId} className="border border-marine/15 bg-white rounded p-2">
          <div className="flex items-start justify-between gap-2">
            <p className="text-xs text-ink whitespace-pre-wrap break-words flex-1">{item.comment}</p>
            <span className="flex shrink-0 items-center gap-1">
              <FeedbackCategoryBadge category={item.category} />
              <FeedbackStatusBadge status={item.status} />
            </span>
          </div>
          <div className="mt-1 text-[11px] text-ink-mid">
            {item.submittedBy.name ?? item.submittedBy.email ?? 'Someone at your organization'} · {formatWhen(item.submittedAt)}
            {item.screen && <> · {item.screen.title || item.screen.path}</>}
          </div>
          <ResolutionLine item={item} />
        </li>
      ))}
    </ul>
  )
}

/**
 * The form behind the feedback button: a comment, a switch to include the
 * screen the person is on (shown in full before it is sent, so there is no
 * mystery about what "context" means), and what this organization has sent
 * before with how each item was resolved.
 */
export function FeedbackDrawer({ api, details, aboveInspector = false, onClose }: FeedbackDrawerProps) {
  const queryClient = useQueryClient()
  const commentId = useId()
  const categoryId = useId()
  const [comment, setComment] = useState('')
  const [category, setCategory] = useState<FeedbackCategory>(DEFAULT_FEEDBACK_CATEGORY)
  const [includeScreen, setIncludeScreen] = useState(true)
  // Captured once when the drawer opens, so the preview and what is sent are the same thing.
  const [screen] = useState(() => captureScreen(api.area, details))
  const [sent, setSent] = useState<FeedbackItem | null>(null)

  const mineQuery = useQuery({ queryKey: api.queryKey, queryFn: api.listMine })
  const submit = useMutation({
    mutationFn: () => api.submit({ comment: comment.trim(), category, ...(includeScreen ? { screen } : {}) }),
    onSuccess: (item) => {
      setSent(item)
      setComment('')
      setCategory(DEFAULT_FEEDBACK_CATEGORY)
      void queryClient.invalidateQueries({ queryKey: api.queryKey })
    },
  })

  useEffect(() => {
    function closeOnEscape(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [onClose])

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    if (comment.trim() === '' || submit.isPending) {
      return
    }
    submit.mutate()
  }

  const remaining = COMMENT_MAX_LENGTH - comment.length
  const chosen = feedbackCategoryOption(category)

  return (
    <aside
      role="dialog"
      aria-label="Product feedback"
      // The page behind is near-white, so the panel stands off it the way the app's headers do: a dark marine band, a solid
      // marine edge and a deep shadow, rather than a white card on a white page.
      className={`fixed ${aboveInspector ? 'bottom-28' : 'bottom-16'} right-4 z-50 w-[calc(100vw-2rem)] max-w-md max-h-[80vh] bg-white border-2 border-marine-dk rounded-lg shadow-[0_20px_50px_-12px_rgba(8,64,79,0.55)] flex flex-col overflow-hidden`}
    >
      <div className="bg-marine-dk px-4 py-3 flex items-center justify-between gap-3">
        <div>
          <div className="text-[9px] font-bold uppercase tracking-[2.5px] text-coral">Feedback</div>
          <div className="text-sm font-black text-white">Tell SeeWhoCares what you need</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close feedback"
          className="rounded p-1 text-white/70 hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-coral"
        >
          <X aria-hidden="true" className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-auto px-4 py-3 flex flex-col gap-4 bg-white">
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div>
            <label htmlFor={categoryId} className="block text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1">
              What kind of feedback is this?
            </label>
            <select
              id={categoryId}
              value={category}
              onChange={(event) => setCategory(event.target.value as FeedbackCategory)}
              className="w-full border border-marine/30 rounded px-2 py-1.5 text-xs text-ink bg-white focus:outline-none focus:ring-2 focus:ring-coral"
            >
              {FEEDBACK_CATEGORY_OPTIONS.map((option) => (
                <option key={option.category} value={option.category}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-ink-mid mt-1">{chosen.description}</p>
          </div>
          <div>
            <label htmlFor={commentId} className="block text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1">
              {chosen.placeholder}
            </label>
            <textarea
              id={commentId}
              value={comment}
              maxLength={COMMENT_MAX_LENGTH}
              onChange={(event) => setComment(event.target.value)}
              rows={4}
              placeholder="As much or as little as you like…"
              className="w-full border border-marine/30 rounded px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-coral"
            />
            {remaining < 200 && <div className="text-[10px] text-ink-mid text-right">{remaining} characters left</div>}
          </div>

          <div className="border border-marine/15 rounded p-2 bg-canvas">
            <div className="flex items-center justify-between gap-3">
              <div className="text-[11px] font-bold text-ink">Include this screen</div>
              <Switch checked={includeScreen} onChange={setIncludeScreen} label="Include this screen" />
            </div>
            <p className="text-[10px] text-ink-mid mt-1">Helps the developer find the exact screen. This is everything that would be sent:</p>
            {includeScreen && <ScreenPreview screen={screen} />}
          </div>

          {submit.isError && (
            <p className="text-[11px] font-bold text-coral">{submit.error instanceof Error && submit.error.message ? submit.error.message : 'Could not send the feedback.'}</p>
          )}
          {sent && !submit.isPending && (
            <p className="text-[11px] font-bold text-green">
              Sent. Thank you - it is on SeeWhoCares's list{sent.submittedBy.name ? ` as ${sent.submittedBy.name}` : ''}.
            </p>
          )}

          <Button icon={Send} type="submit" disabled={comment.trim() === '' || submit.isPending} className="self-end">
            {submit.isPending ? 'Sending…' : 'Send feedback'}
          </Button>
        </form>

        {api.area !== 'worker' && (
        <section className="-mx-4 -mb-3 border-t border-marine/15 bg-marine-lt/40 px-4 py-3">
          <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-2">
            Sent so far <EndpointHint>{api.area === 'admin' ? 'GET /admin/feedback' : 'GET /feedback'}</EndpointHint>
          </div>
          {mineQuery.isPending ? (
            <Skeleton />
          ) : mineQuery.isError ? (
            <LoadFailure size="xs" message="Could not load earlier feedback." onRetry={() => void mineQuery.refetch()} retrying={mineQuery.isFetching} />
          ) : (
            <MyFeedbackList items={mineQuery.data} />
          )}
        </section>
        )}
      </div>
    </aside>
  )
}
